import { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { FormInput } from '@/Components/shared/FormInput';
import type { VehicleFormData } from '@/features/contracts/types/vehicle.types';
import { useVehicleSearch } from '@/features/contracts/hooks/useVehicleSearch';

interface VehicleFieldsProps {
  vehicle: VehicleFormData;
  onChange: (vehicle: VehicleFormData) => void;
  errors?: Record<string, string>;
  societyId?: string;
}

/**
 * Formate automatiquement la saisie d'immatriculation (AA-123-BB)
 */
function formatRegistration(input: string): string {
  let clean = input.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (clean.length > 2) clean = clean.slice(0, 2) + '-' + clean.slice(2);
  if (clean.length > 6) clean = clean.slice(0, 6) + '-' + clean.slice(6);
  if (clean.length > 9) clean = clean.slice(0, 9);
  return clean;
}

/**
 * Formate la saisie VIN (max 17 caractères alphanumériques)
 */
function formatVin(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17);
}

type SearchMode = 'registration' | 'vin' | 'manual';

/**
 * Champs de formulaire pour les informations véhicule
 * 3 modes : Immatriculation (SIV), VIN (SIV), Manuelle
 */
const EMPTY_VEHICLE: Omit<VehicleFormData, 'kmStart'> = {
  registration: '',
  serialNumber: '',
  brand: '',
  model: '',
  finishes: '',
  entryIntoService: '',
  fuel: '',
  fiscalPower: '',
  displacement: '',
  enginePower: '',
  vehicleSearchType: undefined,
};

export function VehicleFields({ vehicle, onChange, errors, societyId }: VehicleFieldsProps) {
  const [searchSuccess, setSearchSuccess] = useState(!!(vehicle.brand && vehicle.entryIntoService));
  const [searchMode, setSearchMode] = useState<SearchMode>('registration');
  const [savedSearch, setSavedSearch] = useState<{ vehicle: VehicleFormData; success: boolean } | null>(null);
  const vehicleSearch = useVehicleSearch();

  const handleChange = (field: keyof VehicleFormData, value: string) => {
    // Invalidate saved search if user types in manual mode
    if (searchMode === 'manual' && savedSearch) {
      setSavedSearch(null);
    }
    onChange({ ...vehicle, [field]: value });
  };

  const handleRegistrationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = searchMode === 'registration'
      ? formatRegistration(e.target.value)
      : formatVin(e.target.value);
    handleChange('registration', formatted);
  };

  const handleSearchVehicle = async () => {
    if (!vehicle.registration?.trim()) return;

    setSearchSuccess(false);

    try {
      const searchType = searchMode === 'registration' ? 1 : 2;
      const result = await vehicleSearch.mutateAsync({
        searchValue: vehicle.registration.trim(),
        type: searchType,
        societyId,
      });

      onChange({
        ...vehicle,
        registration: result.registration || vehicle.registration,
        serialNumber: result.serialNumber || vehicle.serialNumber,
        entryIntoService: result.entryIntoService || vehicle.entryIntoService,
        brand: result.brand || vehicle.brand,
        model: result.model || vehicle.model,
        finishes: result.finishes || vehicle.finishes,
        fuel: result.fuel || vehicle.fuel,
        fiscalPower: result.fiscalPower || vehicle.fiscalPower,
        displacement: result.displacement || vehicle.displacement,
        enginePower: result.enginePower || vehicle.enginePower,
        vehicleSearchType: searchType,
      });

      setSearchSuccess(true);
      // Invalidate saved search when a VIN search succeeds
      if (searchMode === 'vin' && savedSearch) {
        setSavedSearch(null);
      }
    } catch (error) {
      // L'erreur est gérée par le hook useVehicleSearch
    }
  };

  const handleSwitchMode = (mode: SearchMode) => {
    if (mode === searchMode) return;

    // Save current search data when leaving registration mode with a successful search
    if (searchMode === 'registration' && searchSuccess) {
      setSavedSearch({ vehicle: { ...vehicle }, success: true });
    }

    // Restore saved data when coming back to registration mode
    if (mode === 'registration' && savedSearch) {
      setSearchMode(mode);
      setSearchSuccess(savedSearch.success);
      vehicleSearch.reset();
      onChange(savedSearch.vehicle);
      setSavedSearch(null);
      return;
    }

    // Default: clear everything
    setSearchMode(mode);
    setSearchSuccess(false);
    vehicleSearch.reset();
    onChange({ ...vehicle, ...EMPTY_VEHICLE });
  };

  const isSearch = searchMode !== 'manual';

  const tabClass = (mode: SearchMode) =>
    `tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-md tw-transition-all disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ${
      searchMode === mode
        ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
        : 'tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700'
    }`;

  const tabs = (
    <div className="tw-flex tw-flex-wrap tw-bg-gray-100/80 tw-rounded-lg tw-p-0.5">
      <button
        type="button"
        onClick={() => handleSwitchMode('registration')}
        disabled={vehicleSearch.isLoading}
        className={tabClass('registration')}
      >
        Recherche par Immatriculation
      </button>
      <button
        type="button"
        onClick={() => handleSwitchMode('vin')}
        disabled={vehicleSearch.isLoading}
        className={tabClass('vin')}
      >
        Recherche par VIN
      </button>
      <button
        type="button"
        onClick={() => handleSwitchMode('manual')}
        disabled={vehicleSearch.isLoading}
        className={tabClass('manual')}
      >
        <span className="tw-inline-flex tw-items-center tw-gap-1.5">
          Saisie Manuelle
        </span>
      </button>
    </div>
  );

  return (
    <div className="tw-space-y-5">
      {/* Mode Recherche (Immatriculation / VIN) */}
      {isSearch && (
        <div className={"tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-4 sm:tw-p-6 tw-border-l-4 tw-border-l-grey"}>
          {tabs}
          <p className="tw-text-sm tw-text-gray-500 tw-mt-4 tw-mb-4">
            {searchMode === 'registration'
              ? "Saisissez l'immatriculation pour identifier automatiquement le véhicule"
              : 'Saisissez le numéro VIN pour identifier automatiquement le véhicule'}
          </p>

          {/* Champ de saisie */}
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-stretch sm:tw-items-center tw-gap-3">
            {searchMode === 'registration' ? (
              /* Plaque d'immatriculation française */
              <div
                className={`tw-flex tw-items-stretch tw-flex-1 tw-h-14 tw-rounded-lg tw-overflow-hidden tw-shadow-sm tw-transition-all tw-border-2
                  ${errors?.registration ? 'tw-border-red-500' : 'tw-border-gray-300'}
                  focus-within:tw-border-affinicar-primary focus-within:tw-shadow-md`}
              >
                {/* Bande bleue gauche EU */}
                <div className="tw-w-10 tw-bg-[#003399] tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-shrink-0 tw-gap-1.5">
                  <svg width="20" height="20" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
                    {/* 12 étoiles EU en cercle */}
                    {Array.from({ length: 12 }).map((_, i) => {
                      const angle = (i * 30 - 90) * (Math.PI / 180);
                      const cx = 30 + 22 * Math.cos(angle);
                      const cy = 30 + 22 * Math.sin(angle);
                      return (
                        <polygon
                          key={i}
                          points="0,-4 1.2,-1.2 4,-1.5 2,0.8 2.5,4 0,2.2 -2.5,4 -2,0.8 -4,-1.5 -1.2,-1.2"
                          fill="#FFCC00"
                          transform={`translate(${cx},${cy})`}
                        />
                      );
                    })}
                  </svg>
                  <span className="tw-text-white tw-text-[10px] tw-font-bold tw-leading-none">F</span>
                </div>
                {/* Input central */}
                <input
                  type="text"
                  value={vehicle.registration || ''}
                  onChange={handleRegistrationChange}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearchVehicle(); } }}
                  placeholder="AA-123-BB"
                  maxLength={9}
                  className="tw-flex-1 tw-border-0 tw-outline-none tw-bg-white tw-px-4 tw-h-full tw-text-center tw-font-bold tw-text-xl sm:tw-text-2xl tw-tracking-[0.15em] sm:tw-tracking-[0.25em] tw-uppercase"
                />
                {/* Bande bleue droite */}
                <div className="tw-w-10 tw-bg-[#003399] tw-flex-shrink-0" />
              </div>
            ) : (
              /* Input VIN classique */
              <div
                className={`tw-flex tw-items-center tw-flex-1 tw-border-2 tw-rounded-lg tw-bg-white tw-h-14 tw-shadow-sm tw-transition-all
                  ${errors?.registration ? 'tw-border-red-500' : 'tw-border-gray-300'}
                  focus-within:tw-border-affinicar-primary focus-within:tw-shadow-md`}
              >
                <input
                  type="text"
                  value={vehicle.registration || ''}
                  onChange={handleRegistrationChange}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearchVehicle(); } }}
                  placeholder="VF1RFB00468234567"
                  maxLength={17}
                  className="tw-flex-1 tw-border-0 tw-outline-none tw-bg-transparent tw-px-4 tw-h-full tw-text-center tw-font-bold tw-text-xl tw-tracking-widest tw-uppercase"
                />
              </div>
            )}
            {/* Bouton Rechercher */}
            <button
              type="button"
              onClick={handleSearchVehicle}
              disabled={!vehicle.registration?.trim() || vehicleSearch.isLoading}
              className="tw-h-14 tw-px-8 tw-rounded-lg tw-bg-affinicar-primary tw-text-white hover:tw-bg-affinicar-dark tw-transition-colors tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-font-medium tw-text-sm disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-w-full sm:tw-w-auto"
            >
              {vehicleSearch.isLoading ? (
                <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin" />
              ) : (
                <Search className="tw-w-5 tw-h-5" />
              )}
              <span className="tw-hidden sm:tw-inline">Rechercher</span>
            </button>
          </div>

          {errors?.registration && (
            <div className="tw-text-red-500 tw-text-sm tw-mt-2">{errors.registration}</div>
          )}

          {/* Résultats de recherche */}
          {searchSuccess && (
            <div className="tw-mt-4 tw-bg-white tw-border tw-border-affinicar-primary/20 tw-rounded-xl tw-p-4">
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                <svg className="tw-w-5 tw-h-5 tw-text-affinicar-primary tw-flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="tw-text-sm tw-font-semibold tw-text-affinicar-primary">Véhicule identifié</span>
              </div>
              <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2">
                {searchMode === 'registration' && vehicle.registration && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">Immatriculation</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">{vehicle.registration}</p>
                  </div>
                )}
                {searchMode === 'vin' && vehicle.serialNumber && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">VIN</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">{vehicle.serialNumber}</p>
                  </div>
                )}
                {vehicle.brand && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">Marque</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">{vehicle.brand}</p>
                  </div>
                )}
                {vehicle.model && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">Modèle</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">{vehicle.model}</p>
                  </div>
                )}
                {vehicle.finishes && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">Version</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">{vehicle.finishes}</p>
                  </div>
                )}
                {vehicle.fuel && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">Énergie</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">{vehicle.fuel}</p>
                  </div>
                )}
                {vehicle.entryIntoService && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">Mise en circulation</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">
                      {new Date(vehicle.entryIntoService).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {vehicleSearch.isError && (
            <div className="tw-flex tw-items-center tw-gap-2 tw-mt-3 tw-px-3 tw-py-2 tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg">
              <span className="tw-text-sm tw-font-medium tw-text-orange-700">Véhicule non trouvé — passez en mode Manuelle pour remplir les champs</span>
            </div>
          )}
        </div>
      )}

      {/* Mode Manuelle */}
      {!isSearch && (
        <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-4 sm:tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
          {tabs}
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          <FormInput
            label="Immatriculation"
            value={vehicle.registration || ''}
            onChange={(val) => handleChange('registration', formatRegistration(val))}
            placeholder="AA-123-BB"
            error={errors?.registration}
            tooltip="Numéro d'immatriculation du véhicule"
          />
          <FormInput
            label="VIN (N° de série)"
            value={vehicle.serialNumber || ''}
            onChange={(val) => handleChange('serialNumber', formatVin(val))}
            placeholder="VF1RFB00468234567"
            error={errors?.serialNumber}
            tooltip="Numéro de série unique du véhicule (17 caractères)"
          />
          <FormInput
            label="Marque"
            value={vehicle.brand || ''}
            onChange={(val) => handleChange('brand', val)}
            placeholder="Peugeot"
            required
            error={errors?.brand}
            tooltip="Marque du véhicule (ex : Renault, Peugeot, Opel...)"
          />
          <FormInput
            label="Modèle"
            value={vehicle.model || ''}
            onChange={(val) => handleChange('model', val)}
            placeholder="308"
            required
            error={errors?.model}
            tooltip="Modèle exact du véhicule (ex : Clio, 308, Meriva...)"
          />
          <FormInput
            label="Version"
            value={vehicle.finishes || ''}
            onChange={(val) => handleChange('finishes', val)}
            placeholder="GT Line"
            tooltip="Version ou finition du véhicule si connue"
          />
          <FormInput
            label="Mise en circulation"
            type="date"
            value={vehicle.entryIntoService || ''}
            onChange={(val) => handleChange('entryIntoService', val)}
            required
            min="1900-01-01"
            max={`${new Date().getFullYear() + 1}-12-31`}
            error={errors?.entryIntoService}
            tooltip="Date de première mise en circulation figurant sur la carte grise"
          />
          </div>
        </div>
      )}
    </div>
  );
}
